// "Remove this piece" — and the question that has to come with it.
//
// The old Remove button sent `Breakdown: remove sub-issue 3; fold its scope
// into the others.` It was labelled "Entfernen"/"Remove", and the half the user
// never saw decided, on their behalf, that the work goes somewhere else. That
// was not a copy bug: the server had no way to represent any other answer.
// Every must-have on the parent had to end up in some piece, so removing a
// piece could only ever redistribute — which is how dropping one sub-issue
// re-carved every other one.
//
// So the question is asked instead of answered. For each must-have this piece
// covers that NO OTHER piece covers, the user says where it goes: onto a
// surviving piece, or out of scope entirely. A must-have that is already shared
// needs no question — it survives on its other owner — so a piece covering only
// shared ones is a single confirm.
//
// The one thing this surface must NOT do is let a park strand a sibling. A
// shared must-have taken out of scope leaves EVERY owner, so it can empty a
// different piece than the one being removed. That is shown here, before the
// choice, rather than surfacing later as a server deny for a consequence the
// user was never told about.

import { useState } from "react";

import { useT } from "../i18n";

/** One decision per must-have that needs a destination. */
export interface RemoveDecision {
  /** Zero-based index into the card's `criteria`. */
  readonly criterionIndex: number;
  readonly to: { readonly kind: "child"; readonly childId: string } | { readonly kind: "park" };
}

export interface RemoveChildDialogProps {
  readonly childTitle: string;
  /** Must-haves this piece uniquely covers — the ones needing a destination. */
  readonly orphans: ReadonlyArray<{ readonly index: number; readonly text: string }>;
  /** Surviving pieces the work can move to. */
  readonly siblings: ReadonlyArray<{
    readonly childId: string;
    readonly title: string;
    readonly coveredCriteria: ReadonlyArray<number>;
  }>;
  /** True when the parent card is already applied — parking is refused there,
   *  because the issue description is on GitLab and there is no post-apply
   *  must-have write lane. */
  readonly parkDisabled: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (decisions: ReadonlyArray<RemoveDecision>) => void;
}

const PARK = "__park__";

export function RemoveChildDialog({
  childTitle,
  orphans,
  siblings,
  parkDisabled,
  onCancel,
  onConfirm,
}: RemoveChildDialogProps) {
  const t = useT();
  // Default every must-have to the first surviving piece, NOT to park: the
  // destructive option is never the one a distracted user gets by pressing
  // Enter. Empty siblings can't happen (the reducer refuses a split below two).
  const [choice, setChoice] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      orphans.map((o) => [o.index, siblings[0]?.childId ?? PARK]),
    ),
  );

  // Which siblings this set of choices would leave with nothing to deliver.
  // Parking a SHARED must-have strips it from every owner, so the blast radius
  // reaches past the piece being removed.
  const parkedIndices = new Set(
    orphans.filter((o) => choice[o.index] === PARK).map((o) => o.index),
  );
  const stranded = siblings.filter((s) => {
    const gains = orphans.filter(
      (o) => choice[o.index] === s.childId,
    ).length;
    const kept = s.coveredCriteria.filter((i) => !parkedIndices.has(i)).length;
    return kept + gains === 0;
  });

  const confirmable = stranded.length === 0;

  return (
    <div
      className="bd-remove"
      role="group"
      aria-label={t("breakdown.remove.title", { title: childTitle })}
    >
      <p className="bd-remove-head">
        {t("breakdown.remove.title", { title: childTitle })}
      </p>
      {orphans.length === 0 ? (
        <p className="bd-remove-note">{t("breakdown.remove.noOrphans")}</p>
      ) : (
        <>
          <p className="bd-remove-note">{t("breakdown.remove.where")}</p>
          <ul className="bd-remove-list">
            {orphans.map((o) => (
              <li key={o.index}>
                <span className="bd-remove-crit">{o.text}</span>
                <select
                  aria-label={t("breakdown.remove.destinationFor", { text: o.text })}
                  value={choice[o.index] ?? ""}
                  onChange={(e) =>
                    setChoice((prev) => ({ ...prev, [o.index]: e.target.value }))
                  }
                >
                  {siblings.map((s) => (
                    <option key={s.childId} value={s.childId}>
                      {s.title}
                    </option>
                  ))}
                  {/* The answer the prose lane could not express at all. */}
                  {!parkDisabled && (
                    <option value={PARK}>{t("breakdown.remove.park")}</option>
                  )}
                </select>
              </li>
            ))}
          </ul>
        </>
      )}
      {parkDisabled && (
        <p className="bd-remove-note is-warn">{t("breakdown.remove.parkDisabled")}</p>
      )}
      {stranded.length > 0 && (
        <p className="bd-remove-note is-warn">
          {t("breakdown.remove.strands", {
            titles: stranded.map((s) => `"${s.title}"`).join(", "),
          })}
        </p>
      )}
      <div className="bd-remove-actions">
        <button type="button" className="bd-btn is-ghost" onClick={onCancel}>
          {t("breakdown.cancel")}
        </button>
        <button
          type="button"
          className="bd-btn is-danger"
          disabled={!confirmable}
          onClick={() =>
            onConfirm(
              orphans.map((o) => {
                const picked = choice[o.index];
                return {
                  criterionIndex: o.index,
                  to:
                    picked === undefined || picked === PARK
                      ? ({ kind: "park" } as const)
                      : ({ kind: "child", childId: picked } as const),
                };
              }),
            )
          }
        >
          {t("breakdown.remove.confirm")}
        </button>
      </div>
    </div>
  );
}
